import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { DollarSign, Save, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const WithdrawalFeesPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fees, setFees] = useState({
    crypto_withdrawal_fee_percentage: 0,
    crypto_withdrawal_min_fee: 0,
    mobile_money_fee_percentage: 0,
    mobile_money_fixed_fee: 0,
    bank_transfer_fee_percentage: 0,
    bank_transfer_fixed_fee: 0,
    minimum_withdrawal_amount: 0
  });

  useEffect(() => {
    loadFees();
  }, []);

  const loadFees = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/withdrawal-fees`, {
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to load fees');

      const data = await response.json();
      setFees(data.fees || fees);
    } catch (error) {
      console.error('Load fees error:', error);
      toast.error('Failed to load withdrawal fees');
    } finally {
      setLoading(false);
    }
  };

  const saveFees = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/withdrawal-fees`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(fees)
      });

      if (!response.ok) throw new Error('Failed to save fees');

      toast.success('Withdrawal fees updated successfully');
    } catch (error) {
      console.error('Save fees error:', error);
      toast.error('Failed to save withdrawal fees');
    } finally {
      setSaving(false);
    }
  };

  const updateFee = (field, value) => {
    setFees({ ...fees, [field]: parseFloat(value) || 0 });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Withdrawal Fees Configuration
            </h1>
            <p className="text-muted-foreground mt-2">
              Set fees for different withdrawal methods
            </p>
          </div>
          <Button variant="outline" onClick={loadFees}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Crypto Withdrawal Fees */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Crypto Withdrawal Fees
            </CardTitle>
            <CardDescription>
              Configure fees for cryptocurrency withdrawals (BTC, USDC, ETH, etc.)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Percentage Fee (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={fees.crypto_withdrawal_fee_percentage}
                  onChange={(e) => updateFee('crypto_withdrawal_fee_percentage', e.target.value)}
                  placeholder="e.g., 0.5"
                />
                <p className="text-xs text-muted-foreground">
                  Applied as a percentage of withdrawal amount
                </p>
              </div>

              <div className="space-y-2">
                <Label>Minimum Fee (USD equivalent)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={fees.crypto_withdrawal_min_fee}
                  onChange={(e) => updateFee('crypto_withdrawal_min_fee', e.target.value)}
                  placeholder="e.g., 5"
                />
                <p className="text-xs text-muted-foreground">
                  Minimum fee charged even if percentage is lower
                </p>
              </div>
            </div>

            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded">
              <p className="text-sm text-blue-800 dark:text-blue-400">
                <strong>Example:</strong> If percentage is 0.5% and minimum is $5, withdrawing $1,000 charges $5 (0.5%), 
                but withdrawing $500 charges $5 (minimum fee).
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Mobile Money Fees */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Mobile Money Fees
            </CardTitle>
            <CardDescription>
              Configure fees for mobile money withdrawals (M-Pesa, Airtel Money, etc.)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Percentage Fee (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={fees.mobile_money_fee_percentage}
                  onChange={(e) => updateFee('mobile_money_fee_percentage', e.target.value)}
                  placeholder="e.g., 1.0"
                />
              </div>

              <div className="space-y-2">
                <Label>Fixed Fee (local currency)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={fees.mobile_money_fixed_fee}
                  onChange={(e) => updateFee('mobile_money_fixed_fee', e.target.value)}
                  placeholder="e.g., 50"
                />
              </div>
            </div>

            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded">
              <p className="text-sm text-blue-800 dark:text-blue-400">
                <strong>Total Fee = </strong> (Amount × Percentage) + Fixed Fee
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Bank Transfer Fees */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Bank Transfer Fees
            </CardTitle>
            <CardDescription>
              Configure fees for bank transfer withdrawals
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Percentage Fee (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={fees.bank_transfer_fee_percentage}
                  onChange={(e) => updateFee('bank_transfer_fee_percentage', e.target.value)}
                  placeholder="e.g., 0.5"
                />
              </div>

              <div className="space-y-2">
                <Label>Fixed Fee</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={fees.bank_transfer_fixed_fee}
                  onChange={(e) => updateFee('bank_transfer_fixed_fee', e.target.value)}
                  placeholder="e.g., 10"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Global Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Global Withdrawal Settings</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label>Minimum Withdrawal Amount (USD equivalent)</Label>
              <Input
                type="number"
                step="1"
                value={fees.minimum_withdrawal_amount}
                onChange={(e) => updateFee('minimum_withdrawal_amount', e.target.value)}
                placeholder="e.g., 10"
              />
              <p className="text-xs text-muted-foreground">
                Users cannot withdraw less than this amount (converted to their currency)
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button onClick={saveFees} disabled={saving} size="lg">
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Configuration'}
          </Button>
        </div>

        {/* Summary */}
        <Card className="bg-gray-50 dark:bg-gray-900/50">
          <CardHeader>
            <CardTitle className="text-lg">Current Fee Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Crypto Withdrawal:</span>
              <span className="font-medium">{fees.crypto_withdrawal_fee_percentage}% (min ${fees.crypto_withdrawal_min_fee})</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Mobile Money:</span>
              <span className="font-medium">{fees.mobile_money_fee_percentage}% + {fees.mobile_money_fixed_fee}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Bank Transfer:</span>
              <span className="font-medium">{fees.bank_transfer_fee_percentage}% + {fees.bank_transfer_fixed_fee}</span>
            </div>
            <div className="flex justify-between pt-2 border-t">
              <span className="text-muted-foreground">Minimum Withdrawal:</span>
              <span className="font-medium">${fees.minimum_withdrawal_amount}</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default WithdrawalFeesPage;
